import '@/utils/bugsnag';
import { updatePage } from '@/utils/update-page';
import AccordionGroup from '~/components/accordion-group';
import Blaze from '~/components/blaze/index';
import Carousel from '~/components/carousel';
import FeaturedReviews from '~/components/featured-reviews';
import FullWidthVideo from '~/components/full-width-video';
import Header from '~/components/header';
import LinkCarousel from '~/components/link-carousel';
import ListCarousel from '~/components/list-carousel';
import NewsletterSignup from '~/components/newsletter-signup';
import ProductCarousel from '~/components/product-carousel';
import ProductCategoryCarousel from '~/components/product-category-carousel';
import ProductFeature from '~/components/product-feature';
import Quote from '~/components/quote';
import UI from '~/components/ui-state/index';
import Cart from '~/components/cart/index';
import VideoPlayer from '~/components/video-player/index';
import UpdatePage from '@/components/update-page/index';
import CollectionMenu from '@/components/collection-menu/index';
import DraggableScroll from '@/components/draggable-scroll';
import IngredientModal from '@/components/ingredient-modal';

import { UserInteractionEvents } from '~/types';
import { updateSearchPage } from '@/utils/update-search-page';
import { 
  setupPredictiveSearchLazyLoading, 
  setupLSGBundleLazyLoading,
  loadProductReviews,
  loadStickyAddToCart,
  loadMobileMenu,
  loadLSGProductView
} from '@/utils/lazy-loaders';


import '../../assets/lsg-mega-menu.js';

const init = async () => {
  const { default: Alpine } = await import('alpinejs');
  const { default: validate } = await import(
    '@colinaut/alpinejs-plugin-simple-validate'
  );

  // Load conditional components
  const [
    ProductReviews,
    StickyAddToCart,
    LSGProductView,
    MobileMenu,
    { default: AlpineIntersect },
    { default: AlpineCollapse },
    { default: ProductView },
    { default: NotifyMeModal },
    { default: Tooltip },
    { default: ProductQuality },
    { default: ProductQuickview },
    { default: Search }
  ] = await Promise.all([
    loadProductReviews(),
    loadStickyAddToCart(),
    loadLSGProductView(),
    loadMobileMenu(Alpine),
    import('@alpinejs/intersect'),
    import('@alpinejs/collapse'),
    import('@/components/product-view'),
    import('@/components/notify-me-modal'),
    import('@/components/tooltip'),
    import('@/components/product-quality'),
    import('@/components/product-quickview'),
    import('@/components/search')
  ]);

  Alpine.plugin(AlpineIntersect);
  Alpine.plugin(AlpineCollapse);

  Alpine.plugin(validate);
  Alpine.plugin(Blaze);
  Alpine.plugin(VideoPlayer);

  Alpine.plugin(UI);
  Alpine.plugin(Cart);
  Alpine.plugin(NewsletterSignup);
  Alpine.plugin(AccordionGroup);
  Alpine.plugin(Carousel);
  Alpine.plugin(ListCarousel);
  Alpine.plugin(Header);
  Alpine.plugin(LinkCarousel);
  Alpine.plugin(FeaturedReviews);
  Alpine.plugin(ProductFeature);
  Alpine.plugin(Quote);
  Alpine.plugin(ProductCarousel);
  Alpine.plugin(ProductCategoryCarousel);
  Alpine.plugin(FullWidthVideo);
  Alpine.plugin(ProductView);
  if (LSGProductView) {
    Alpine.plugin(LSGProductView);
  }
  Alpine.plugin(NotifyMeModal);
  if (StickyAddToCart) {
    Alpine.plugin(StickyAddToCart);
  }
  Alpine.plugin(Tooltip);
  Alpine.plugin(ProductQuality);
  Alpine.plugin(MobileMenu);
  Alpine.plugin(ProductQuickview);
  Alpine.plugin(Search);
  if (ProductReviews) {
    Alpine.plugin(ProductReviews);
  }
  Alpine.plugin(UpdatePage);
  Alpine.plugin(CollectionMenu);
  Alpine.plugin(DraggableScroll);
  Alpine.plugin(IngredientModal);

  const searchBody = document.querySelector("body");
  if (searchBody?.classList.contains("search")) {
    Alpine.data('updateSearchPage', updateSearchPage);
  }

  window.Alpine = Alpine;
  window.updatePage = updatePage;
  if (searchBody?.classList.contains("search")) {
    (window as any).updateSearchPage = updateSearchPage;
  }

  Alpine.start();

  // Setup lazy loading for various components
  setupPredictiveSearchLazyLoading();
  setupLSGBundleLazyLoading();
};

const timeout = setTimeout(() => {
  _eventHandler();
}, 800);

const _eventHandler = () => {
  clearTimeout(timeout);

  Object.values(UserInteractionEvents).forEach(event =>
    document.removeEventListener(event, _eventHandler, {
      capture: true,
    })
  );
};

init();

const _addEventListeners = (event: UserInteractionEvents) => {
  document.addEventListener(event, _eventHandler, {
    capture: true,
  });
};

Object.values(UserInteractionEvents).forEach(event => {
  _addEventListeners(event);
});
